import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import type { HoverPointerSnapshot } from "@/features/hoverIdle/input/hoverPointerTypes";
import { resolveHoverActive } from "@/features/hoverIdle/input/resolveHoverActive";
import { getMessages } from "@/i18n/runtime";

interface ToastEntry {
  readonly element: HTMLDivElement;
  readonly closeTarget: HTMLSpanElement;
  readonly groupKey: string | null;
  readonly onPointerEnter: (event: PointerEvent) => void;
  readonly onPointerLeave: () => void;
  readonly startFade: () => void;
  readonly cancelFade: () => void;
  virtualPointerInside: boolean;
  fadeTimer: ReturnType<typeof setTimeout> | null;
}

export interface ShowToastOptions {
  readonly dismissAfterHoverMs?: number;
  readonly groupKey?: string;
  readonly maxVisibleCount?: number;
}

export interface NoticeToastStackOptions {
  readonly pointerModel?: HoverPointerModel;
}

export class NoticeToastStack implements Disposable {
  private readonly stack: HTMLDivElement;
  private readonly entries = new Set<ToastEntry>();
  private readonly pointerModel: HoverPointerModel | null;
  private readonly unsubscribePointer: (() => void) | null;

  constructor(options?: NoticeToastStackOptions) {
    this.pointerModel = options?.pointerModel ?? null;

    this.stack = document.createElement("div");
    this.stack.className = "hover-idle-toast-stack";
    this.stack.setAttribute("role", "status");
    this.stack.setAttribute("aria-live", "polite");
    document.body.append(this.stack);

    this.unsubscribePointer = this.pointerModel
      ? this.pointerModel.subscribe((snapshot) => {
          this.handlePointerSnapshot(snapshot);
        })
      : null;
  }

  show(message: string, options?: ShowToastOptions): void {
    const element = document.createElement("div");
    element.className = "hover-idle-toast";

    const body = document.createElement("div");
    body.className = "hover-idle-toast-body";
    body.textContent = message;
    element.append(body);

    const dismissAfterHoverMs = options?.dismissAfterHoverMs;
    const groupKey = options?.groupKey ?? null;
    const isDismissible =
      typeof dismissAfterHoverMs === "number" && dismissAfterHoverMs > 0;

    if (!isDismissible) {
      element.style.pointerEvents = "none";
      this.stack.append(element);
      return;
    }

    element.dataset.dismissible = "true";
    element.style.pointerEvents = "none";

    const closeTarget = document.createElement("span");
    closeTarget.className = "hover-idle-toast-close";
    closeTarget.textContent = "×";
    closeTarget.setAttribute("role", "img");
    closeTarget.setAttribute("aria-label", getMessages().common.toastCloseAria);
    closeTarget.dataset.virtualPointer = "allow";
    element.append(closeTarget);

    const entry: ToastEntry = {
      element,
      closeTarget,
      groupKey,
      onPointerEnter: (event: PointerEvent): void => {
        if (event.pointerType === "touch") {
          return;
        }
        entry.startFade();
      },
      onPointerLeave: (): void => {
        if (entry.virtualPointerInside) {
          return;
        }
        entry.cancelFade();
      },
      startFade: (): void => {
        if (entry.fadeTimer !== null) {
          return;
        }

        element.style.transition = `opacity ${dismissAfterHoverMs}ms linear`;
        element.style.opacity = "0";

        entry.fadeTimer = setTimeout(() => {
          entry.fadeTimer = null;
          this.removeEntry(entry);
        }, dismissAfterHoverMs);
      },
      cancelFade: (): void => {
        if (entry.fadeTimer === null) {
          return;
        }

        clearTimeout(entry.fadeTimer);
        entry.fadeTimer = null;
        element.style.transition = "opacity 120ms linear";
        element.style.opacity = "1";
      },
      virtualPointerInside: false,
      fadeTimer: null,
    };

    closeTarget.addEventListener("pointerenter", entry.onPointerEnter);
    closeTarget.addEventListener("pointerleave", entry.onPointerLeave);

    this.entries.add(entry);
    this.stack.append(element);
    this.trimVisibleEntries(groupKey, options?.maxVisibleCount);

    if (this.pointerModel !== null) {
      this.handlePointerSnapshot(this.pointerModel.getSnapshot());
    }
  }

  clearAll(): void {
    for (const entry of [...this.entries]) {
      this.removeEntry(entry);
    }
  }

  dispose(): void {
    this.clearAll();
    this.unsubscribePointer?.();
    this.stack.remove();
  }

  private handlePointerSnapshot(snapshot: HoverPointerSnapshot): void {
    for (const entry of this.entries) {
      const inside = this.isVirtualPointerOnEntry(snapshot, entry);

      if (inside === entry.virtualPointerInside) {
        continue;
      }

      entry.virtualPointerInside = inside;

      if (inside) {
        entry.startFade();
      } else {
        entry.cancelFade();
      }
    }
  }

  private isVirtualPointerOnEntry(
    snapshot: HoverPointerSnapshot,
    entry: ToastEntry,
  ): boolean {
    if (snapshot.kind !== "virtualTouch" || snapshot.position === null) {
      return false;
    }

    return resolveHoverActive(
      snapshot,
      entry.closeTarget.getBoundingClientRect(),
    );
  }

  private trimVisibleEntries(
    groupKey: string | null,
    maxVisibleCount: number | undefined,
  ): void {
    if (
      maxVisibleCount === undefined ||
      !Number.isFinite(maxVisibleCount) ||
      maxVisibleCount < 1
    ) {
      return;
    }

    const visibleLimit = Math.floor(maxVisibleCount);
    const groupEntries = [...this.entries].filter(
      (entry) => entry.groupKey === groupKey,
    );
    const removeCount = groupEntries.length - visibleLimit;
    if (removeCount <= 0) {
      return;
    }

    for (const entry of groupEntries.slice(0, removeCount)) {
      this.removeEntry(entry);
    }
  }

  private removeEntry(entry: ToastEntry): void {
    if (!this.entries.has(entry)) {
      return;
    }

    this.entries.delete(entry);

    if (entry.fadeTimer !== null) {
      clearTimeout(entry.fadeTimer);
      entry.fadeTimer = null;
    }

    entry.closeTarget.removeEventListener("pointerenter", entry.onPointerEnter);
    entry.closeTarget.removeEventListener("pointerleave", entry.onPointerLeave);
    entry.element.remove();
  }
}
